import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocale } from '@/hooks/useLocale';
import { apiErrorMessage } from '@/services/apiClient';
import { useLabelSheetQuery } from '../api/catalog.api';
import type { LabelPrintState } from '../labels/labelLines';
import { LABEL_PRESETS, pageRule, type LabelPresetId } from '../labels/labelPresets';
import { LabelSheetView } from '../labels/LabelSheetView';

function isPrintState(value: unknown): value is LabelPrintState {
  if (typeof value !== 'object' || value === null) return false;
  const state = value as { request?: { items?: unknown }; presetId?: unknown };
  return (
    Array.isArray(state.request?.items) &&
    typeof state.presetId === 'string' &&
    state.presetId in LABEL_PRESETS
  );
}

/**
 * The labels alone, outside the application shell, sized to the stock.
 *
 * The job arrives in navigation state from the label builder, never in the address, and is read from
 * the cache the preview filled. Reopened without it — a reload, a shared link — it says so and sends
 * the reader back rather than printing anything it cannot vouch for.
 */
export function LabelPrintPage() {
  const { t } = useTranslation();
  const { locale, direction } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const state = isPrintState(location.state) ? location.state : null;
  const sheetQuery = useLabelSheetQuery(state?.request ?? null);

  const back = () => void navigate('/inventory/labels', { state: state?.builder });

  if (!state) {
    return (
      <main dir={direction} className="print-status label-print">
        <p>{t('catalog.labels.printExpired')}</p>
        <div className="print-toolbar">
          <button type="button" onClick={back}>
            {t('catalog.labels.backToBuilder')}
          </button>
        </div>
      </main>
    );
  }

  if (sheetQuery.isPending) return <p className="print-status">{t('common.loading')}</p>;
  if (sheetQuery.isError) {
    return (
      <main dir={direction} className="print-status label-print">
        <p>{apiErrorMessage(sheetQuery.error) ?? t('catalog.labels.previewError')}</p>
        <div className="print-toolbar">
          <button type="button" onClick={back}>
            {t('catalog.labels.backToBuilder')}
          </button>
        </div>
      </main>
    );
  }

  const preset = LABEL_PRESETS[state.presetId as LabelPresetId];

  return (
    <main className="label-print" data-testid="label-print">
      <style>{pageRule(preset)}</style>
      <div className="print-toolbar" dir={direction}>
        <span className="label-print__summary">
          {t('catalog.labels.total', { count: sheetQuery.data.totalLabels })}
        </span>
        <button type="button" onClick={back}>
          {t('catalog.labels.backToBuilder')}
        </button>
        <button type="button" onClick={() => window.print()}>
          {t('catalog.labels.print')}
        </button>
      </div>
      <LabelSheetView sheet={sheetQuery.data} preset={preset} locale={locale} />
    </main>
  );
}
